"use client";
import React, { useState, useCallback, useMemo } from "react";
import { Button, Col, Divider, Flex, Form, Input, Row, Typography } from "antd";
import type { UploadFile } from "antd";
import { isValidPhoneNumber } from "libphonenumber-js";
import Radio from "antd/es/radio";
import { PhoneInput } from "@openings/ui";
import type { ScheduleSummary } from "../LocationStep";
import Uploader from "./Uploader";
import type { MediaUploadFn } from "./Uploader";

const { TextArea } = Input;
const { Paragraph } = Typography;

const isValidEmail = (value: string) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

/** Minimal service shape required by the consultation request form. */
export interface ConsultationService {
  id: string;
  title: string;
  option?: { id: string; title: string } | undefined;
}

/** Payload sent to the host app's submit handler. */
export interface ServiceRequestPayload {
  salonId: string;
  userId?: string;
  teamMemberId?: string;
  scheduleId?: string;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  note?: string;
  userPhotos: string[];
  referencePhotos: string[];
  serviceIds: string[];
  serviceTitles: string[];
}

interface RequestFormProps {
  salonId: string;
  userId?: string;
  teamMemberId?: string;
  scheduleId?: string;
  /**
   * The member's schedules, used when `scheduleId` is not already resolved.
   * One option is used as-is; several make the customer choose, so a request
   * never reaches the business without a schedule.
   */
  scheduleOptions?: ScheduleSummary[];
  services: ConsultationService[];
  onClose: () => void;
  submitRequest: (payload: ServiceRequestPayload) => Promise<void>;
  uploadMedia: MediaUploadFn;
}

const RequestForm: React.FC<RequestFormProps> = ({
  salonId,
  userId,
  teamMemberId,
  scheduleId,
  scheduleOptions = [],
  services,
  onClose,
  submitRequest,
  uploadMedia,
}) => {
  const needsScheduleChoice = !scheduleId && scheduleOptions.length > 1;
  const [chosenScheduleId, setChosenScheduleId] = useState<string>();
  const resolvedScheduleId =
    scheduleId ??
    (scheduleOptions.length === 1 ? scheduleOptions[0].id : chosenScheduleId);
  const scheduleLabel = scheduleOptions.every((option) =>
    option.address?.trim(),
  )
    ? "Location"
    : "Schedule";

  const [values, setValues] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: "",
    userPhotos: [] as UploadFile[],
    referencePhotos: [] as UploadFile[],
    note: "",
  });

  const {
    firstName,
    lastName,
    email,
    phoneNumber,
    userPhotos,
    referencePhotos,
    note,
  } = values;

  const [isLoading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState({
    firstName: false,
    lastName: false,
    email: false,
    phoneNumber: false,
    note: false,
    schedule: false,
  });

  const onChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const { name, value } = e.target;
      const nextValue =
        typeof value === "string" ? value : value === undefined ? "" : value;
      setValues((prev) => ({ ...prev, [name]: nextValue }));
    },
    [],
  );

  const onUploadChange = useCallback(
    (type: "userPhotos" | "referencePhotos", fileList: UploadFile[]) => {
      setValues((prev) => ({ ...prev, [type]: fileList }));
    },
    [],
  );

  const resolveUploadedUrls = useCallback((files: UploadFile[]) => {
    return files
      .map((file) => {
        const response = file.response as { url?: string } | undefined;
        if (response?.url) return response.url;
        if (typeof file.url === "string" && file.url.trim().length > 0) {
          return file.url;
        }
        return undefined;
      })
      .filter((url): url is string => Boolean(url));
  }, []);

  const serviceMeta = useMemo(() => {
    const ids = services.map((service) => service.option?.id ?? service.id);
    const titles = services.map((service) =>
      service.option?.title
        ? `${service.title} - ${service.option.title}`
        : service.title,
    );
    return { ids, titles };
  }, [services]);

  const onSendRequest = async () => {
    const normalizedEmail = email.trim();
    const normalizedPhone = phoneNumber.trim();
    const hasPhone = normalizedPhone.length > 0;
    const errors = {
      firstName: !firstName.trim(),
      lastName: !lastName.trim(),
      email: !normalizedEmail || !isValidEmail(normalizedEmail),
      phoneNumber: !hasPhone || !isValidPhoneNumber(normalizedPhone),
      note: !note.trim(),
      schedule: needsScheduleChoice && !resolvedScheduleId,
    };
    setFieldErrors(errors);
    if (Object.values(errors).some(Boolean)) return;

    setLoading(true);
    setErrorMessage(null);

    try {
      await submitRequest({
        userId: userId || undefined,
        teamMemberId: teamMemberId || undefined,
        scheduleId: resolvedScheduleId || undefined,
        salonId,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: normalizedEmail,
        phoneNumber: normalizedPhone,
        note: note.trim() || undefined,
        userPhotos: resolveUploadedUrls(userPhotos),
        referencePhotos: resolveUploadedUrls(referencePhotos),
        serviceIds: serviceMeta.ids,
        serviceTitles: serviceMeta.titles,
      });

      setSent(true);
    } catch (e: any) {
      setErrorMessage(e?.message || "Could not send request");
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <>
        <Paragraph>{`Your request has been forwarded. We'll get back to you shortly.`}</Paragraph>
        <Button color="default" variant="filled" onClick={onClose}>
          Close
        </Button>
      </>
    );
  }

  return (
    <>
      {errorMessage && <Paragraph type="danger">{errorMessage}</Paragraph>}
      <Paragraph>
        {`The selected service must be reviewed before an appointment can be booked. Submit the form and we'll get back to you shortly:`}
        <br />
        <br />
        <ul style={{ listStyleType: "square", paddingLeft: 20 }}>
          {services.map((service) => (
            <li key={service.title}>{service.title}</li>
          ))}
        </ul>
      </Paragraph>

      <Form layout="vertical" labelWrap={false}>
        {needsScheduleChoice && (
          <Form.Item
            label={scheduleLabel}
            validateStatus={fieldErrors.schedule ? "error" : undefined}
          >
            <Radio.Group
              value={chosenScheduleId}
              onChange={(e) => {
                setChosenScheduleId(e.target.value);
                if (fieldErrors.schedule)
                  setFieldErrors((p) => ({ ...p, schedule: false }));
              }}
            >
              {/* antd's Radio.Group does not expose a group role itself. */}
              <Flex vertical gap={8} role="radiogroup" aria-label={scheduleLabel}>
                {scheduleOptions.map((option) => (
                  <Radio key={option.id} value={option.id}>
                    {option.title}
                    {option.address?.trim() && (
                      <Paragraph
                        type="secondary"
                        style={{ margin: 0, fontSize: "0.875em" }}
                      >
                        {option.address}
                      </Paragraph>
                    )}
                  </Radio>
                ))}
              </Flex>
            </Radio.Group>
          </Form.Item>
        )}

        {/* Describe Request */}
        <Form.Item label="Describe your request" htmlFor="note">
          <TextArea
            id="note"
            name="note"
            value={note}
            onChange={(e) => {
              onChange(e);
              if (fieldErrors.note)
                setFieldErrors((p) => ({ ...p, note: false }));
            }}
            status={fieldErrors.note ? "error" : ""}
            placeholder="Describe the service you're looking for, and any preferences or requirements the team member should know about."
            // The field grows with the text instead of scrolling inside.
            autoSize={{ minRows: 3, maxRows: 10 }}
          />
        </Form.Item>

        {/* User Photos */}
        <Form.Item label="Photos that may help">
          <Paragraph type="secondary">
            Clear photos of what you have now help the business understand your
            request.
          </Paragraph>
          <Uploader
            fileList={userPhotos}
            setFileList={(list) => onUploadChange("userPhotos", list)}
            uploadMedia={uploadMedia}
          />
        </Form.Item>

        {/* Reference Photos */}
        <Form.Item label="Reference photos">
          <Paragraph type="secondary">
            Provide photos of the result you have in mind to help the team
            member understand your preferences.
          </Paragraph>
          <Uploader
            fileList={referencePhotos}
            setFileList={(list) => onUploadChange("referencePhotos", list)}
            uploadMedia={uploadMedia}
          />
        </Form.Item>

        <Divider dashed />

        {/* First & Last Name */}
        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item label="First name" htmlFor="firstName">
              <Input
                id="firstName"
                name="firstName"
                value={firstName}
                onChange={(e) => {
                  onChange(e);
                  if (fieldErrors.firstName)
                    setFieldErrors((p) => ({ ...p, firstName: false }));
                }}
                status={fieldErrors.firstName ? "error" : ""}
                placeholder="First Name"
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item label="Last name" htmlFor="lastName">
              <Input
                id="lastName"
                name="lastName"
                value={lastName}
                onChange={(e) => {
                  onChange(e);
                  if (fieldErrors.lastName)
                    setFieldErrors((p) => ({ ...p, lastName: false }));
                }}
                status={fieldErrors.lastName ? "error" : ""}
                placeholder="Last Name"
              />
            </Form.Item>
          </Col>
        </Row>

        {/* Email */}
        <Form.Item label="Email" htmlFor="email">
          <Input
            id="email"
            name="email"
            value={email}
            onChange={(e) => {
              onChange(e);
              if (fieldErrors.email)
                setFieldErrors((p) => ({ ...p, email: false }));
            }}
            status={fieldErrors.email ? "error" : ""}
            placeholder="you@example.com"
            inputMode="email"
          />
        </Form.Item>

        {/* Phone Number */}
        <Form.Item label="Phone Number" htmlFor="phoneNumber">
          <PhoneInput
            accentColor="#000000"
            value={phoneNumber}
            status={fieldErrors.phoneNumber ? "error" : ""}
            onChange={(v) => {
              onChange({ target: { name: "phoneNumber", value: v } } as any);
              if (fieldErrors.phoneNumber)
                setFieldErrors((p) => ({ ...p, phoneNumber: false }));
            }}
          />
        </Form.Item>
      </Form>

      <Divider dashed />

      {/* Submit Button */}
      <Button
        disabled={
          isLoading ||
          (needsScheduleChoice && !resolvedScheduleId) ||
          !email.trim() ||
          !isValidEmail(email) ||
          !phoneNumber.trim() ||
          !isValidPhoneNumber(phoneNumber.trim()) ||
          !firstName.trim() ||
          !lastName.trim() ||
          !note.trim()
        }
        onClick={onSendRequest}
        loading={isLoading}
        type="primary"
      >
        Request
      </Button>
    </>
  );
};

export default React.memo(RequestForm);
